import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Chip,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import LicenseContext from 'src/contexts/LicenseContext';
import { HowToPlanning, PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

type DispatchModel = 'opus' | 'repro' | 'fusion';

type Props = {
  screenName: string;
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  onClose: () => void;
  selectedDeliveryIds: number[];
  selectedOrderIds: number[];
  startOn: string;
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
  updateTraceMode: (bool: boolean) => void;
  updateTraceOnly: (bool: boolean) => void;
  updateTraceCompanyId: (id: number) => void;
};

const dispatchModelDefs: { value: DispatchModel; label: string; description: string }[] = [
  {
    value: 'opus',
    label: 'LOG Opus',
    description: '従来の最適化ロジックに基づき、全体効率を重視した配車を行います。',
  },
  {
    value: 'repro',
    label: 'LOG Repro',
    description:
      '過去の配車実績をもとに、これまでの配車パターンを再現する配車を行います。実績にない条件の納品先は、割り当て精度が下がる場合があります。',
  },
  {
    value: 'fusion',
    label: 'LOG Fusion',
    description:
      '過去の配車パターンを再現しながら、全体の最適化も考慮して配車を行います。再現性と配車完了率を両立したモデルです。',
  },
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 4,
          height: 16,
          borderRadius: 1,
          bgcolor: 'primary.main',
          flexShrink: 0,
        }}
      />
      <Typography variant="subtitle2" fontWeight="bold">
        {children}
      </Typography>
    </Box>
  );
}

type ConstraintRowProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  unit: string;
  max?: number;
  disabled?: boolean;
};

function ConstraintRow({ label, value, onChange, unit, max = 120, disabled = false }: ConstraintRowProps) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: '4rem', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Slider
        size="small"
        value={value}
        onChange={(_, v) => { if (typeof v === 'number') onChange(v); }}
        step={5}
        min={0}
        max={max}
        disabled={disabled}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        type="number"
        inputProps={{ min: 0, max, step: 1 }}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = Math.min(max, Math.max(0, Number(e.target.value) || 0));
          onChange(n);
        }}
        sx={{ width: '5rem' }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '2.5rem', flexShrink: 0 }}>
        {unit}
      </Typography>
    </Stack>
  );
}

type ToggleSwitchRowProps = {
  label: string;
  tooltip?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

function ToggleSwitchRow({ label, tooltip, checked, onChange }: ToggleSwitchRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 0.75,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </Tooltip>
        )}
      </Box>
      <Switch
        size="small"
        checked={checked}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
    </Box>
  );
}

const PlanningPresenter: FC<Props> = memo(
  ({
    screenName,
    updateBalancedLoading,
    updateExcludeToll,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    planningRequest,
    onClose,
    selectedDeliveryIds,
    selectedOrderIds,
    relaxedRules,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    priorityLargeTrucks,
    updateActivateIncrementalMaxNumPistons,
    updateTraceMode,
    updateTraceOnly,
    updateTraceCompanyId,
  }) => {
    const licenseContext = useContext(LicenseContext);

    const [howToPlanning, setHowToPlanning] = useState<HowToPlanning>('最適化');
    const [priorityMode, setPriorityMode] = useState<'none' | 'nearby' | 'far'>('none');
    const [dispatchModel, setDispatchModel] = useState<DispatchModel>('opus');
    type VehicleSizePreference = 'none' | 'small' | 'large';
    const [vehicleSizePreference, setVehicleSizePreference] = useState<VehicleSizePreference>('none');
    const [availabilityOfExpressway, setAvailabilityOfExpressway] = useState<boolean>(false);
    const [includeFutureDeliveries, setIncludeFutureDeliveries] = useState<boolean>(false);
    const [autoSplitOrders, setAutoSplitOrders] = useState<boolean>(false);
    const [dontExchangePreroute, setDontExchangePreroute] = useState<boolean>(true);
    const [concurrentAllOrNothing, setConcurrentAllOrNothing] = useState<boolean>(true);
    const [activateIncrementalMaxNumPistons, setActivateIncrementalMaxNumPistons] = useState<boolean>(false);
    const [expandWorkingTimeStartMinutes, setExpandWorkingTimeStartMinutes] = useState<number>(0);
    const [expandWorkingTimeEndMinutes, setExpandWorkingTimeEndMinutes] = useState<number>(0);
    const [expandLoadStartMinutes, setExpandLoadStartMinutes] = useState<number>(0);
    const [expandLoadEndMinutes, setExpandLoadEndMinutes] = useState<number>(0);
    const [expandUnloadStartMinutes, setExpandUnloadStartMinutes] = useState<number>(0);
    const [expandUnloadEndMinutes, setExpandUnloadEndMinutes] = useState<number>(0);
    const [expandMaxVolumeRate, setExpandMaxVolumeRate] = useState<number>(0);
    const [expandMaxLoadCapacityRate, setExpandMaxLoadCapacityRate] = useState<number>(0);
    const [isExecuting, setIsExecuting] = useState<boolean>(false);
    const [constraintExpanded, setConstraintExpanded] = useState<boolean>(false);

    // 制約セクションに値が設定されている場合は自動で開く（閉じるのはユーザー操作のみ）
    const hasConstraintValues = useMemo(
      () =>
        selectedRelaxedRules.length > 0 ||
        expandWorkingTimeStartMinutes > 0 ||
        expandWorkingTimeEndMinutes > 0 ||
        expandLoadStartMinutes > 0 ||
        expandLoadEndMinutes > 0 ||
        expandUnloadStartMinutes > 0 ||
        expandUnloadEndMinutes > 0 ||
        expandMaxLoadCapacityRate > 0 ||
        expandMaxVolumeRate > 0,
      [
        selectedRelaxedRules,
        expandWorkingTimeStartMinutes,
        expandWorkingTimeEndMinutes,
        expandLoadStartMinutes,
        expandLoadEndMinutes,
        expandUnloadStartMinutes,
        expandUnloadEndMinutes,
        expandMaxLoadCapacityRate,
        expandMaxVolumeRate,
      ],
    );

    useEffect(() => {
      if (hasConstraintValues) setConstraintExpanded(true);
    }, [hasConstraintValues]);

    // dispatchModel -> trace params マッピング
    useEffect(() => {
      const companyId = licenseContext.config?.selected_company_id ?? 0;
      if (dispatchModel === 'opus') {
        updateTraceMode(false);
        updateTraceOnly(false);
        updateTraceCompanyId(0);
      } else if (dispatchModel === 'repro') {
        updateTraceMode(false);
        updateTraceOnly(true);
        updateTraceCompanyId(companyId);
      } else {
        updateTraceMode(true);
        updateTraceOnly(false);
        updateTraceCompanyId(companyId);
      }
    }, [dispatchModel, licenseContext.config?.selected_company_id, updateTraceMode, updateTraceOnly, updateTraceCompanyId]);

    useEffect(() => {
      updateBalancedLoading(howToPlanning === '平準化');
    }, [howToPlanning, updateBalancedLoading]);

    // vehicleSizePreference → priorityLargeTrucks マッピング
    useEffect(() => {
      if (vehicleSizePreference === 'none') setPriorityLargeTrucks(0.5);
      else if (vehicleSizePreference === 'small') setPriorityLargeTrucks(0);
      else setPriorityLargeTrucks(1.0);
    }, [vehicleSizePreference, setPriorityLargeTrucks]);

    useEffect(() => {
      updateExcludeToll(!availabilityOfExpressway);
    }, [availabilityOfExpressway, updateExcludeToll]);

    useEffect(() => {
      updateIncludeFutureDeliveries(includeFutureDeliveries);
    }, [includeFutureDeliveries, updateIncludeFutureDeliveries]);

    useEffect(() => {
      updateAutoSplitOrders(autoSplitOrders);
    }, [autoSplitOrders, updateAutoSplitOrders]);

    useEffect(() => {
      updateDontExchangePreroute(dontExchangePreroute);
    }, [dontExchangePreroute, updateDontExchangePreroute]);

    useEffect(() => {
      updateConcurrentAllOrNothing(concurrentAllOrNothing);
    }, [concurrentAllOrNothing, updateConcurrentAllOrNothing]);

    useEffect(() => {
      updateActivateIncrementalMaxNumPistons(activateIncrementalMaxNumPistons);
    }, [activateIncrementalMaxNumPistons, updateActivateIncrementalMaxNumPistons]);

    useEffect(() => {
      updateExpandWorkingStartMinutes(expandWorkingTimeStartMinutes);
    }, [expandWorkingTimeStartMinutes, updateExpandWorkingStartMinutes]);

    useEffect(() => {
      updateExpandWorkingEndMinutes(expandWorkingTimeEndMinutes);
    }, [expandWorkingTimeEndMinutes, updateExpandWorkingEndMinutes]);

    useEffect(() => {
      updateExpandLoadStartMinutes(expandLoadStartMinutes);
    }, [expandLoadStartMinutes, updateExpandLoadStartMinutes]);

    useEffect(() => {
      updateExpandLoadEndMinutes(expandLoadEndMinutes);
    }, [expandLoadEndMinutes, updateExpandLoadEndMinutes]);

    useEffect(() => {
      updateExpandUnloadStartMinutes(expandUnloadStartMinutes);
    }, [expandUnloadStartMinutes, updateExpandUnloadStartMinutes]);

    useEffect(() => {
      updateExpandUnloadEndMinutes(expandUnloadEndMinutes);
    }, [expandUnloadEndMinutes, updateExpandUnloadEndMinutes]);

    useEffect(() => {
      updateExpandMaxVolumeRate(expandMaxVolumeRate);
    }, [expandMaxVolumeRate, updateExpandMaxVolumeRate]);

    useEffect(() => {
      updateExpandMaxLoadCapacityRate(expandMaxLoadCapacityRate);
    }, [expandMaxLoadCapacityRate, updateExpandMaxLoadCapacityRate]);

    // 制約選択時に緩和値をリセット
    useEffect(() => {
      if (!selectedRelaxedRules?.length) return;
      if (selectedRelaxedRules.includes('労働時間')) {
        setExpandWorkingTimeStartMinutes(0);
        setExpandWorkingTimeEndMinutes(0);
      }
      if (selectedRelaxedRules.includes('指定時間')) {
        setExpandLoadStartMinutes(0);
        setExpandLoadEndMinutes(0);
        setExpandUnloadStartMinutes(0);
        setExpandUnloadEndMinutes(0);
      }
      if (selectedRelaxedRules.includes('重量')) {
        setExpandMaxLoadCapacityRate(0);
      }
      if (selectedRelaxedRules.includes('体積')) {
        setExpandMaxVolumeRate(0);
      }
    }, [selectedRelaxedRules]);

    // localStorage 復元
    useEffect(() => {
      const json = localStorage.getItem('PlanningPresenterSettings');
      if (!json) return;

      const saved = JSON.parse(json) as Partial<PlanningConditions & {
        priorityMode?: 'none' | 'nearby' | 'far';
        vehicleSizePreference?: VehicleSizePreference;
        dispatchModel?: DispatchModel;
      }>;

      if (saved.howToPlanning === '最適化' || saved.howToPlanning === '平準化') {
        setHowToPlanning(saved.howToPlanning);
      }
      if (saved.availabilityOfExpressway !== undefined) setAvailabilityOfExpressway(saved.availabilityOfExpressway);
      if (saved.includeFutureDeliveries !== undefined) setIncludeFutureDeliveries(saved.includeFutureDeliveries);
      if (saved.dontExchangePreroute !== undefined) setDontExchangePreroute(saved.dontExchangePreroute);
      if (saved.concurrentAllOrNothing !== undefined) setConcurrentAllOrNothing(saved.concurrentAllOrNothing);
      if (saved.activateIncrementalMaxNumPistons !== undefined) setActivateIncrementalMaxNumPistons(saved.activateIncrementalMaxNumPistons);
      if (saved.selectedRelaxedRules) setSelectedRelaxedRules(saved.selectedRelaxedRules);
      if (saved.expandWorkingTimeStartMinutes !== undefined) setExpandWorkingTimeStartMinutes(saved.expandWorkingTimeStartMinutes);
      if (saved.expandWorkingTimeEndMinutes !== undefined) setExpandWorkingTimeEndMinutes(saved.expandWorkingTimeEndMinutes);
      if (saved.expandLoadStartMinutes !== undefined) setExpandLoadStartMinutes(saved.expandLoadStartMinutes);
      if (saved.expandLoadEndMinutes !== undefined) setExpandLoadEndMinutes(saved.expandLoadEndMinutes);
      if (saved.expandUnloadStartMinutes !== undefined) setExpandUnloadStartMinutes(saved.expandUnloadStartMinutes);
      if (saved.expandUnloadEndMinutes !== undefined) setExpandUnloadEndMinutes(saved.expandUnloadEndMinutes);
      if (saved.expandMaxVolumeRate !== undefined) setExpandMaxVolumeRate(saved.expandMaxVolumeRate);
      if (saved.expandMaxLoadCapacityRate !== undefined) setExpandMaxLoadCapacityRate(saved.expandMaxLoadCapacityRate);
      if (saved.priorityMode) setPriorityMode(saved.priorityMode);
      if (saved.vehicleSizePreference) setVehicleSizePreference(saved.vehicleSizePreference);
      if (saved.dispatchModel) setDispatchModel(saved.dispatchModel);
    }, [licenseContext.config, setSelectedRelaxedRules]);

    // localStorage 保存
    useEffect(() => {
      const obj = {
        howToPlanning,
        availabilityOfExpressway,
        includeFutureDeliveries,
        dontExchangePreroute,
        concurrentAllOrNothing,
        activateIncrementalMaxNumPistons,
        selectedRelaxedRules,
        expandWorkingTimeStartMinutes,
        expandWorkingTimeEndMinutes,
        expandLoadStartMinutes,
        expandLoadEndMinutes,
        expandUnloadStartMinutes,
        expandUnloadEndMinutes,
        expandMaxVolumeRate,
        expandMaxLoadCapacityRate,
        priorityMode,
        vehicleSizePreference,
        dispatchModel,
      };
      localStorage.setItem('PlanningPresenterSettings', JSON.stringify(obj));
    }, [
      howToPlanning,
      priorityMode,
      vehicleSizePreference,
      availabilityOfExpressway,
      includeFutureDeliveries,
      dontExchangePreroute,
      concurrentAllOrNothing,
      activateIncrementalMaxNumPistons,
      selectedRelaxedRules,
      expandWorkingTimeStartMinutes,
      expandWorkingTimeEndMinutes,
      expandLoadStartMinutes,
      expandLoadEndMinutes,
      expandUnloadStartMinutes,
      expandUnloadEndMinutes,
      expandMaxVolumeRate,
      expandMaxLoadCapacityRate,
      dispatchModel,
    ]);

    const onClick = () => {
      if (isExecuting) return;
      setIsExecuting(true);
      ReactGA.event('executePlanning', { screen_name: screenName, button_name: '自動配車' });
      onClose();
      planningRequest(selectedDeliveryIds, selectedOrderIds);
    };

    const handleChipToggle = (rule: RelaxedRuleVo) => {
      if (selectedRelaxedRules.includes(rule)) {
        setSelectedRelaxedRules(selectedRelaxedRules.filter((r) => r !== rule));
      } else {
        setSelectedRelaxedRules([...selectedRelaxedRules, rule]);
      }
    };

    const useAiPlanning = licenseContext.config?.use_ai_planning === true;

    return (
      <>
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TuneRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold" lineHeight={1.3}>
              自動配車の設定
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            配車条件を設定して自動割り当てを実行します
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: 'auto',
            maxHeight: '80vh',
            px: 3,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* Section 1: 計算方法 */}
          <Box>
            <SectionLabel>計算方法</SectionLabel>
            <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
              <Select
                value={howToPlanning}
                onChange={(e: SelectChangeEvent) => setHowToPlanning(e.target.value as HowToPlanning)}
              >
                <MenuItem value="最適化">最適化 — トラック台数と時間距離を最小化</MenuItem>
                <MenuItem value="平準化">平準化 — 割り付ける荷物を均等化</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Section 2: 優先設定 */}
          <Box>
            <SectionLabel>優先設定</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  エリア優先
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={priorityMode}
                  onChange={(_, v: 'none' | 'nearby' | 'far' | null) => {
                    if (v !== null) setPriorityMode(v);
                  }}
                >
                  {([
                    { value: 'none', label: '指定なし' },
                    { value: 'nearby', label: '近隣' },
                    { value: 'far', label: '遠方' },
                  ] as const).map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  車両サイズ優先
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={vehicleSizePreference}
                  onChange={(_, v: VehicleSizePreference | null) => {
                    if (v !== null) setVehicleSizePreference(v);
                  }}
                >
                  {([
                    { value: 'none', label: '指定なし' },
                    { value: 'small', label: '小型' },
                    { value: 'large', label: '大型' },
                  ] as const).map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <ToggleSwitchRow
                  label="有料道路使用"
                  checked={availabilityOfExpressway}
                  onChange={setAvailabilityOfExpressway}
                />
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <ToggleSwitchRow
                  label="宵積み有効"
                  checked={includeFutureDeliveries}
                  onChange={setIncludeFutureDeliveries}
                />
              </Box>
            </Box>
          </Box>

          {/* Section 3: 配車方式 */}
          <Box>
            <SectionLabel>配車方式</SectionLabel>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {/* ToggleSwitchRow × 2: 2カラムグリッド */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <ToggleSwitchRow
                  label="既存割り当てを維持"
                  tooltip="新しいオーダーを追加配車する際、既存のドライバー割り当てを維持するか、ドライバー間で割り当てを変更するかを選択できます。"
                  checked={dontExchangePreroute}
                  onChange={setDontExchangePreroute}
                />
                <ToggleSwitchRow
                  label="回転数を平準化"
                  tooltip="各車両の回転数をなるべく均等に配分します"
                  checked={activateIncrementalMaxNumPistons}
                  onChange={setActivateIncrementalMaxNumPistons}
                />
              </Box>
              {/* Checkbox × 2: 縦並び・左インデント */}
              <Stack spacing={0} sx={{ pl: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={autoSplitOrders}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setAutoSplitOrders(e.target.checked)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={<Typography variant="body2">積載量超過時に案件を自動分割</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={concurrentAllOrNothing}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setConcurrentAllOrNothing(e.target.checked)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={<Typography variant="body2">同一ロケーションで複数台になる場合は割り当てない</Typography>}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Section 4: モデル選択 (use_ai_planning=trueのときのみ) */}
          {useAiPlanning && (
            <Box>
              <SectionLabel>モデル選択 <Typography component="span" variant="caption" color="text.secondary" fontWeight="normal">（ベータ版）</Typography></SectionLabel>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                {dispatchModelDefs.map((model) => {
                  const selected = dispatchModel === model.value;
                  return (
                    <Card
                      key={model.value}
                      variant="outlined"
                      sx={{
                        flex: 1,
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderWidth: selected ? 2 : 1,
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        position: 'relative',
                      }}
                    >
                      <CardActionArea
                        onClick={() => setDispatchModel(model.value)}
                        sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', height: '100%' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            color={selected ? 'primary.main' : 'text.primary'}
                          >
                            {model.label}
                          </Typography>
                          <Tooltip title={model.description} placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.4,
                          }}
                        >
                          {model.description}
                        </Typography>
                        {selected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                            }}
                          />
                        )}
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Section 5: 制約 (Accordion) */}
          <Accordion
            disableGutters
            elevation={0}
            expanded={constraintExpanded}
            onChange={(_, expanded) => setConstraintExpanded(expanded)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <SectionLabel>制約</SectionLabel>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 無効にする制約 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    無効にする制約
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 0, p: 0.5 }}
                      onClick={() => setSelectedRelaxedRules([...relaxedRules])}
                    >
                      全選択
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 0, p: 0.5 }}
                      onClick={() => setSelectedRelaxedRules([])}
                    >
                      全解除
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {relaxedRules.map((rule) => {
                    const isSelected = selectedRelaxedRules.includes(rule);
                    return (
                      <Chip
                        key={rule}
                        label={rule}
                        size="small"
                        onClick={() => handleChipToggle(rule)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* 制約緩和 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    制約緩和
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ fontSize: '0.65rem', minWidth: 0, p: '2px 6px', lineHeight: 1 }}
                    onClick={() => {
                      setExpandWorkingTimeStartMinutes(0);
                      setExpandWorkingTimeEndMinutes(0);
                      setExpandLoadStartMinutes(0);
                      setExpandLoadEndMinutes(0);
                      setExpandUnloadStartMinutes(0);
                      setExpandUnloadEndMinutes(0);
                      setExpandMaxLoadCapacityRate(0);
                      setExpandMaxVolumeRate(0);
                    }}
                  >
                    リセット
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="勤務開始"
                      value={expandWorkingTimeStartMinutes}
                      onChange={setExpandWorkingTimeStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('労働時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="勤務終了"
                      value={expandWorkingTimeEndMinutes}
                      onChange={setExpandWorkingTimeEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('労働時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="集荷開始"
                      value={expandLoadStartMinutes}
                      onChange={setExpandLoadStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="集荷締切"
                      value={expandLoadEndMinutes}
                      onChange={setExpandLoadEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="納品開始"
                      value={expandUnloadStartMinutes}
                      onChange={setExpandUnloadStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="納品締切"
                      value={expandUnloadEndMinutes}
                      onChange={setExpandUnloadEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="積載重量"
                      value={expandMaxLoadCapacityRate}
                      onChange={setExpandMaxLoadCapacityRate}
                      unit="%"
                      max={100}
                      disabled={selectedRelaxedRules.includes('重量')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="積載体積"
                      value={expandMaxVolumeRate}
                      onChange={setExpandMaxVolumeRate}
                      unit="%"
                      max={100}
                      disabled={selectedRelaxedRules.includes('体積')}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button variant="text" size="small" onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<LocalShippingRoundedIcon />}
            onClick={onClick}
            disabled={isExecuting}
          >
            実行する
          </Button>
        </Box>
      </>
    );
  }
);

export default PlanningPresenter;
